import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  getStartFormFields,
  startProcessByProcessDefinitionId,
} from "../../../../../api/bpm/endpoints";
import { findNextTask } from "../../../../../utils/bpm/nextTask";
import { useAuth } from "../../../../../auth";
import { TaskFormRenderer } from "../../../../../components/TaskExecution/TaskFormRenderer";
import { decodeNavParams } from "../../../../../utils/navParams";
import { useTranslation } from "react-i18next";

const startProcessSearchSchema = z.object({
  from: z.enum(["start", "processes"]).optional().catch("start"),
  fromParams: z.string().optional().catch(""),
});

export const Route = createFileRoute(
  "/__auth/processes/start/$processDefinitionId/execute",
)({
  component: StartProcessExecutePage,
  validateSearch: startProcessSearchSchema,
  staticData: {
    title: "sidebar.processes.start",
  },
});

function StartProcessExecutePage() {
  const { t } = useTranslation();
  const { processDefinitionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loadingNext, setLoadingNext] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    try {
      const ls = localStorage.getItem("token");
      if (ls) {
        const parsed = JSON.parse(ls);
        if (parsed.accessToken) setToken(parsed.accessToken);
        return;
      }
      const ss = sessionStorage.getItem("token");
      if (ss) {
        const parsed = JSON.parse(ss);
        if (parsed.accessToken) setToken(parsed.accessToken);
      }
    } catch (e) {
      console.error("[StartProcess] Failed to parse token", e);
    }
  }, []);

  useEffect(() => {
    const fetchFormData = async () => {
      if (!processDefinitionId) return;
      setLoadingData(true);
      setError(null);
      try {
        const res =
          await getStartFormFields(
            processDefinitionId,
            {
              headers: {
                "X-Api-Key": "7b9nBSEbAYorYo0os2vpW3CaHrg8A3jaceA5eqik",
              } as any
            }
          );
        const data = (res as any)?.data || res;
        console.log("[StartProcess] Form data fetched:", data);
        setFormData(data);
      } catch (err: any) {
        console.error("[StartProcess] Error fetching form data:", err);
        setError(t("processes.executeNotFound"));
      } finally {
        setLoadingData(false);
      }
    };

    fetchFormData();
  }, [processDefinitionId]);

  const handleBack = () => {
    const prevParams = search.fromParams
      ? decodeNavParams(search.fromParams) || {}
      : {};
    navigate({ to: "/processes/start", search: prevParams as any });
  };

  const handleFormSubmit = async (formData: any) => {
    if (!user?.tenant) return;
    setLoadingNext(true);

    const variables = formData?.variables || formData || {};
    try {
      const res =
        await startProcessByProcessDefinitionId(
          user.tenant,
          processDefinitionId,
          { variables },
          {
            headers: {
              "X-Api-Key": "7b9nBSEbAYorYo0os2vpW3CaHrg8A3jaceA5eqik",
            } as any
          }
        );
      
      const result = (res as any)?.data || res;

      const nextTask = await findNextTask({
        tenant: user.tenant,
        processInstanceId: result.id,
        user,
      });

      if (nextTask?.id) {
        navigate({
          to: "/tasks/$taskId/execute",
          params: { taskId: nextTask.id },
          search: {
            from: "start" as const,
            fromParams: search.fromParams || "",
            processInstanceId: result.id,
          },
          replace: true,
        });
      } else {
        setSuccessMessage(t("processes.startSuccess"));
        setTimeout(() => handleBack(), 2000);
      }
    } catch (err: any) {
      console.error("[StartProcess] Error starting process:", err);
      setErrorMessage(t("processes.startError"));
      setLoadingNext(false);
    }
  };

  const successSnackbar = (
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity="success" onClose={() => setSuccessMessage(null)}>
        {successMessage}
      </Alert>
    </Snackbar>
  );

  if (loadingNext) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t("common.processing")}
        </Typography>
        {successSnackbar}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: "50vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Typography variant="h5" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t("processes.executeBackMessage")}
        </Typography>
        <Box
          component="button"
          onClick={handleBack}
          sx={{
            px: 4,
            py: 1.5,
            bgcolor: "primary.main",
            color: "white",
            border: "none",
            borderRadius: 1,
            cursor: "pointer",
            fontSize: "1rem",
            "&:hover": { bgcolor: "primary.dark" },
          }}
        >
          {t("processes.executeBackButton")}
        </Box>
      </Box>
    );
  }

  if (loadingData || !formData) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "calc(100vh - 64px)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, mb: 2, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {formData?.activityName || t("processDefinitions.startProcess")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={handleBack}
              >
                {t("processes.startNewProcess")}
              </Typography>
              {" > "}
              <span>{formData?.originalProcessName || processDefinitionId}</span>
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("processes.processInitiator")}:
            </Typography>
            <Typography variant="body2">
              {user?.name || user?.email || "-"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <TaskFormRenderer
          processDefinitionId={processDefinitionId}
          token={token}
          user={user}
          taskForm={formData}
          taskVariables={{}}
          onBack={handleBack}
          onSubmit={(data) => handleFormSubmit(data?.variables || data || {})}
        />
      </Box>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
